export default class Synthesizer {
  constructor(scene) {
    this.scene = scene;
    this.ctx = scene.sound.context;

    // Master gain
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.5; // default volume
    this.masterGain.connect(this.ctx.destination);

    // Bus separati: MUSICA (drone) ed EFFETTI (SFX), controllabili in modo
    // indipendente dal menu impostazioni.
    // Default allineati a quelli del menu impostazioni (SettingsMenu.js).
    this._musicEnabled = true;
    this._musicVolume = 0.7; // 0..1
    this._sfxEnabled = true;
    this._sfxVolume = 0.8; // 0..1

    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = this._musicVolume;
    this.musicGain.connect(this.masterGain);

    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = this._sfxVolume;
    this.sfxGain.connect(this.masterGain);

    // Suoni persistenti
    this.droneNodes = null; // array di nodi del drone (per stopDrone)
    this.boostNoise = null;
    this.boostGain = null;
  }

  // --- CONTROLLI VOLUME / ON-OFF (menu impostazioni) ---
  _applyMusicGain() {
    const now = this.ctx.currentTime;
    const target = this._musicEnabled ? this._musicVolume : 0;
    this.musicGain.gain.setTargetAtTime(target, now, 0.02);
  }

  _applySfxGain() {
    const now = this.ctx.currentTime;
    const target = this._sfxEnabled ? this._sfxVolume : 0;
    this.sfxGain.gain.setTargetAtTime(target, now, 0.02);
  }

  setMusicEnabled(on) { this._musicEnabled = !!on; this._applyMusicGain(); }
  setMusicVolume(v) { this._musicVolume = Math.max(0, Math.min(1, v)); this._applyMusicGain(); }
  setSfxEnabled(on) { this._sfxEnabled = !!on; this._applySfxGain(); }
  setSfxVolume(v) { this._sfxVolume = Math.max(0, Math.min(1, v)); this._applySfxGain(); }

  applySettings({ musicEnabled, musicVolume, sfxEnabled, sfxVolume } = {}) {
    if (typeof musicEnabled === 'boolean') this._musicEnabled = musicEnabled;
    if (typeof musicVolume === 'number') this._musicVolume = Math.max(0, Math.min(1, musicVolume));
    if (typeof sfxEnabled === 'boolean') this._sfxEnabled = sfxEnabled;
    if (typeof sfxVolume === 'number') this._sfxVolume = Math.max(0, Math.min(1, sfxVolume));
    this._applyMusicGain();
    this._applySfxGain();
  }

  // Sblocca il contesto audio se sospeso (autoplay policy del browser).
  ensureRunning() {
    if (this.ctx.state === 'suspended' && this.ctx.resume) {
      this.ctx.resume().catch(() => {});
    }
  }

  // Genera un buffer di white noise riutilizzabile.
  makeNoiseBuffer(seconds = 1) {
    const size = Math.floor(this.ctx.sampleRate * seconds);
    const buffer = this.ctx.createBuffer(1, size, this.ctx.sampleRate);
    const out = buffer.getChannelData(0);
    for (let i = 0; i < size; i++) out[i] = Math.random() * 2 - 1;
    return buffer;
  }

  // --- 1. COLONNA SONORA: DRONE AMBIENTALE SCURO (low-frequency hum) ---
  // Nessuna melodia: solo un ronzio cupo e continuo, con un leggerissimo
  // movimento dato dal battimento tra due oscillatori quasi identici.
  startDrone() {
    if (this.droneNodes) return;
    this.ensureRunning();

    const nodes = [];

    // Filtro che tiene tutto sul cupo.
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 120;
    filter.Q.value = 0.7;

    const droneGain = this.ctx.createGain();
    droneGain.gain.value = 0.14; // sottofondo, non invadente
    filter.connect(droneGain);
    droneGain.connect(this.musicGain);

    // Due oscillatori bassi leggermente scordati -> battimento lento.
    [39.0, 41.2].forEach((freq) => {
      const osc = this.ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      osc.connect(filter);
      osc.start();
      nodes.push(osc);
    });

    // Sub-bass sinusoidale per dare "peso" al ronzio.
    const sub = this.ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = 26;
    const subGain = this.ctx.createGain();
    subGain.gain.value = 0.5;
    sub.connect(subGain);
    subGain.connect(filter);
    sub.start();
    nodes.push(sub);

    // LFO lentissimo che apre/chiude appena il filtro (respiro).
    const lfo = this.ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.08;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 40; // +/- 40Hz sulla cutoff
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start();
    nodes.push(lfo);

    this.droneNodes = nodes;
    this.droneGain = droneGain;
  }

  stopDrone() {
    if (!this.droneNodes) return;
    this.droneNodes.forEach((n) => {
      try { n.stop(); } catch (e) {}
      try { n.disconnect(); } catch (e) {}
    });
    this.droneNodes = null;
  }

  // --- 2. EAT: DIGITAL BLIP (corto e piacevole) ---
  // soft=true per il cibo base: si mangia di continuo, quindi va tenuto piu'
  // basso e piu' grave, altrimenti stanca. I drop mantengono il blip pieno.
  playEat(soft = false) {
    this.ensureRunning();
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    // Piccolo blip digitale ascendente: pulito e gradevole.
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(soft ? 560 : 880, t);
    osc.frequency.exponentialRampToValueAtTime(soft ? 760 : 1320, t + 0.06);

    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(soft ? 0.025 : 0.06, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0005, t + (soft ? 0.07 : 0.09));

    osc.connect(gain);
    gain.connect(this.sfxGain);
    osc.start(t);
    osc.stop(t + 0.1);
  }

  // --- 3. BOOST: SCARICA ELETTRICA CONTINUA ---
  startBoost() {
    if (this.boostNoise) return;
    this.ensureRunning();

    this.boostNoise = this.ctx.createBufferSource();
    this.boostNoise.buffer = this.makeNoiseBuffer(2);
    this.boostNoise.loop = true;

    // Bandpass che scolpisce il rumore in uno sfrigolio elettrico.
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 3200;
    filter.Q.value = 6;

    // Modulazione veloce e squadrata -> crackle da scarica.
    const filterLfo = this.ctx.createOscillator();
    filterLfo.type = 'square';
    filterLfo.frequency.value = 32;
    const filterLfoGain = this.ctx.createGain();
    filterLfoGain.gain.value = 1800;
    filterLfo.connect(filterLfoGain);
    filterLfoGain.connect(filter.frequency);

    // Buzz elettrico di supporto (corpo del suono).
    const buzz = this.ctx.createOscillator();
    buzz.type = 'sawtooth';
    buzz.frequency.value = 70;
    const buzzGain = this.ctx.createGain();
    buzzGain.gain.value = 0.02;

    this.boostGain = this.ctx.createGain();
    this.boostGain.gain.setValueAtTime(0.0001, this.ctx.currentTime);
    this.boostGain.gain.exponentialRampToValueAtTime(0.05, this.ctx.currentTime + 0.05);

    this.boostNoise.connect(filter);
    filter.connect(this.boostGain);
    buzz.connect(buzzGain);
    buzzGain.connect(this.boostGain);
    this.boostGain.connect(this.sfxGain);

    this.boostNoise.start();
    filterLfo.start();
    buzz.start();

    // Salviamo i nodi accessori per fermarli in stopBoost.
    this._boostExtra = [filterLfo, buzz];
  }

  // Smonta tutto quello che questo synth ha attaccato al contesto audio.
  //
  // Serve perche' l'AudioContext e' dell'app e sopravvive alla partita (vedi
  // game/audioContext.js): prima ci pensava la chiusura del contesto a fare
  // piazza pulita, e senza questo metodo il drone continuerebbe a suonare in
  // lobby e se ne sommerebbe uno nuovo a ogni ingresso in partita.
  destroy() {
    this.stopDrone();
    this.stopBoost();
    // I nodi accessori del boost si fermano dopo 80ms (dissolvenza): scollegare
    // subito il master li rende comunque inudibili.
    try { this.masterGain.disconnect(); } catch (e) {}
    try { this.musicGain.disconnect(); } catch (e) {}
    try { this.sfxGain.disconnect(); } catch (e) {}
    this.scene = null;
  }

  stopBoost() {
    if (this.boostNoise) {
      const now = this.ctx.currentTime;
      // Piccola dissolvenza per evitare il "click" di stop.
      if (this.boostGain) {
        this.boostGain.gain.cancelScheduledValues(now);
        this.boostGain.gain.setValueAtTime(this.boostGain.gain.value, now);
        this.boostGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
      }
      const noise = this.boostNoise;
      const extra = this._boostExtra || [];
      setTimeout(() => {
        try { noise.stop(); } catch (e) {}
        try { noise.disconnect(); } catch (e) {}
        extra.forEach((n) => { try { n.stop(); } catch (e) {} try { n.disconnect(); } catch (e) {} });
      }, 80);
      this.boostNoise = null;
      this._boostExtra = null;
    }
  }

  // --- 4. DEATH: GLITCH + WHITE NOISE (TV che si spegne) ---
  playDeath() {
    this.ensureRunning();
    const t = this.ctx.currentTime;

    // (a) Glitch: salti di frequenza casuali che collassano.
    const glitch = this.ctx.createOscillator();
    glitch.type = 'sawtooth';
    const glitchGain = this.ctx.createGain();
    glitchGain.gain.setValueAtTime(0.25, t);
    glitchGain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    for (let i = 0; i < 8; i++) {
      glitch.frequency.setValueAtTime(Math.random() * 1200 + 120, t + i * 0.03);
    }
    glitch.connect(glitchGain);
    glitchGain.connect(this.sfxGain);
    glitch.start(t);
    glitch.stop(t + 0.35);

    // (b) Burst di white noise che si spegne rapidamente.
    const noise = this.ctx.createBufferSource();
    noise.buffer = this.makeNoiseBuffer(0.6);
    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0.35, t);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    noise.connect(noiseGain);
    noiseGain.connect(this.sfxGain);
    noise.start(t);
    noise.stop(t + 0.4);

    // (c) "TV spenta": tono acuto che precipita verso il basso e collassa
    // in un punto (la classica riga che si chiude nel tubo catodico).
    const tv = this.ctx.createOscillator();
    tv.type = 'sine';
    tv.frequency.setValueAtTime(1800, t + 0.3);
    tv.frequency.exponentialRampToValueAtTime(50, t + 0.55);
    const tvGain = this.ctx.createGain();
    tvGain.gain.setValueAtTime(0.0001, t + 0.3);
    tvGain.gain.exponentialRampToValueAtTime(0.3, t + 0.33);
    tvGain.gain.exponentialRampToValueAtTime(0.0005, t + 0.6);
    tv.connect(tvGain);
    tvGain.connect(this.sfxGain);
    tv.start(t + 0.3);
    tv.stop(t + 0.62);
  }

  // --- 5. POWERUP: RELOAD / POWER CHARGING ---
  playPowerup() {
    this.ensureRunning();
    const t = this.ctx.currentTime;

    // (a) Charging: sweep ascendente con filtro che si apre.
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(120, t);
    osc.frequency.linearRampToValueAtTime(1400, t + 0.35);

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(200, t);
    filter.frequency.exponentialRampToValueAtTime(4000, t + 0.35);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.linearRampToValueAtTime(0.18, t + 0.2);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.42);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain);
    osc.start(t);
    osc.stop(t + 0.42);

    // (b) Reload: "cha-chunk" meccanico a fine carica (due click di rumore).
    [0.36, 0.46].forEach((offset) => {
      const click = this.ctx.createBufferSource();
      click.buffer = this.makeNoiseBuffer(0.05);
      const clickFilter = this.ctx.createBiquadFilter();
      clickFilter.type = 'bandpass';
      clickFilter.frequency.value = 1500;
      clickFilter.Q.value = 1.2;
      const clickGain = this.ctx.createGain();
      clickGain.gain.setValueAtTime(0.22, t + offset);
      clickGain.gain.exponentialRampToValueAtTime(0.001, t + offset + 0.04);
      click.connect(clickFilter);
      clickFilter.connect(clickGain);
      clickGain.connect(this.sfxGain);
      click.start(t + offset);
      click.stop(t + offset + 0.05);
    });
  }
}
